import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { X, ExternalLink, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ad display interval: 25 minutes of scrolling
const AD_INTERVAL_MS = 25 * 60 * 1000; // 25 minutes

/**
 * MarketplaceAd - Displays an ad in the marketplace feed
 */
export function MarketplaceAd({ category, className = '', onClose }) {
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  
  useEffect(() => {
    fetchAd();
  }, [category]);
  
  const fetchAd = async () => {
    try {
      const params = category ? `?category=${category}` : '';
      const response = await axios.get(`${API}/advertising/serve${params}`);
      setAd(response.data.ad);
    } catch (err) {
      console.error('Failed to fetch ad:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleClick = async () => {
    if (!ad) return;
    
    // Record click
    try {
      await axios.post(`${API}/advertising/click/${ad.ad_id}`);
    } catch (err) {
      console.error('Failed to record click:', err);
    }
    
    // Open link
    window.open(ad.link_url, '_blank', 'noopener,noreferrer');
  };
  
  const handleDismiss = () => {
    setDismissed(true);
    if (onClose) onClose();
  };
  
  if (loading || !ad || dismissed) return null;
  
  return (
    <Card className={`relative overflow-hidden border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50 ${className}`} data-testid="marketplace-ad">
      <CardContent className="p-3 sm:p-4">
        {/* Sponsored Badge */}
        <div className="absolute top-2 left-2">
          <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800 border-yellow-300">
            <Sparkles className="w-3 h-3 mr-1" />
            Sponsored
          </Badge>
        </div>
        
        {/* Close Button */}
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 rounded-full bg-white/80 hover:bg-white text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="Dismiss ad"
        >
          <X className="w-4 h-4" />
        </button>
        
        {/* Ad Content */}
        <div 
          className="flex gap-3 sm:gap-4 pt-6 cursor-pointer"
          onClick={handleClick}
        >
          {ad.image_url && (
            <img
              src={ad.image_url}
              alt={ad.title}
              className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg flex-shrink-0"
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900 line-clamp-1 sm:line-clamp-2">
              {ad.title}
            </h4>
            <p className="text-sm text-gray-600 line-clamp-2 mt-1">
              {ad.description}
            </p>
            <div className="flex items-center gap-1 text-blue-600 text-sm mt-2">
              <span>Learn more</span>
              <ExternalLink className="w-3 h-3" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * useAdTimer - Hook to track scrolling time and show ads
 */
export function useAdTimer(hasSubscription = false) {
  const [showAd, setShowAd] = useState(false);
  const scrollTimeRef = useRef(0);
  const lastScrollRef = useRef(Date.now());
  const intervalRef = useRef(null);
  
  useEffect(() => {
    if (hasSubscription) {
      setShowAd(false);
      return;
    }
    
    // Track active scrolling time
    const handleScroll = () => {
      const now = Date.now();
      const timeSinceLastScroll = now - lastScrollRef.current;
      
      // Only count if scrolling within 5 seconds
      if (timeSinceLastScroll < 5000) {
        scrollTimeRef.current += timeSinceLastScroll;
      }
      
      lastScrollRef.current = now;
      
      // Check if we should show an ad
      if (scrollTimeRef.current >= AD_INTERVAL_MS) {
        setShowAd(true);
        scrollTimeRef.current = 0;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasSubscription]);
  
  const dismissAd = useCallback(() => {
    setShowAd(false);
  }, []);
  
  return { showAd, dismissAd };
}

/**
 * AdFreeSubscriptionBanner - Upsell banner for ad-free subscription
 */
export function AdFreeSubscriptionBanner({ onSubscribe }) {
  const { user, getAuthHeaders } = useAuth();
  const [subscribing, setSubscribing] = useState(false);
  
  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const response = await axios.post(`${API}/advertising/subscribe/ad-free`, {}, {
        headers: getAuthHeaders()
      });
      
      if (onSubscribe) onSubscribe(response.data);
    } catch (err) {
      console.error('Subscription failed:', err);
    } finally {
      setSubscribing(false);
    }
  };
  
  return (
    <Card className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white" data-testid="ad-free-banner">
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-bold text-lg">Go Ad-Free</h3>
            <p className="text-purple-100 text-sm mt-1">
              Enjoy the marketplace without interruptions for just $1.50/month
            </p>
          </div>
          <Button
            onClick={handleSubscribe}
            disabled={subscribing}
            className="bg-white text-purple-600 hover:bg-purple-50 font-semibold whitespace-nowrap"
          >
            {subscribing ? 'Processing...' : 'Subscribe - $1.50/mo'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * AdStatusChecker - Check if user has ad-free subscription
 */
export function useAdFreeStatus() {
  const { user, getAuthHeaders } = useAuth();
  const [hasSubscription, setHasSubscription] = useState(false);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    checkSubscription();
  }, [user]);
  
  const checkSubscription = async () => {
    if (!user) {
      setHasSubscription(false);
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/advertising/subscription/status`, {
        headers: getAuthHeaders()
      });
      setHasSubscription(response.data.has_subscription);
    } catch (err) {
      setHasSubscription(false);
    } finally {
      setLoading(false);
    }
  };
  
  return { hasSubscription, loading, refresh: checkSubscription };
}

export default MarketplaceAd;
